import { useState } from "react";
import {
	leaveForAirport,
	preparationDue,
	tripCalendar,
	type TripWorkspace,
} from "@japangolmok/core";
import { editions, packingSuggestions } from "@japangolmok/content";
import type { PersistTrip } from "./TripExperience";
import { downloadTripFile } from "./TripTransfer";
import { CopyText } from "./ShareSheet";
import { isAppsInTossChannel } from "../api-url";
import { track } from "../analytics/events";
export function StayEditor({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const [notice, setNotice] = useState("");
	return (
		<details className="workspace-disclosure">
			<summary>숙소 이름·일본어 주소 적어 두기</summary>
			<p>
				이 기기에만 저장해요. 공유 링크에는 기본으로 빼요. 예약 확인 메일의
				일본어 주소를 붙여 넣어요.
			</p>
			<form
				className="compact-form"
				onSubmit={async (e) => {
					e.preventDefault();
					const d = new FormData(e.currentTarget);
					if (
						(await persist((t) => {
							t.stay = {
								name: String(d.get("name")).trim().slice(0, 80),
								addressJa: String(d.get("address")).trim().slice(0, 300),
								note: String(d.get("note")).trim().slice(0, 500),
							};
						})) !== false
					)
						setNotice("숙소를 이 기기에 저장했어요.");
				}}
			>
				<label>
					숙소 이름
					<input
						name="name"
						maxLength={80}
						defaultValue={trip.stay?.name ?? ""}
					/>
				</label>
				<label>
					일본어 주소
					<textarea
						name="address"
						maxLength={300}
						defaultValue={trip.stay?.addressJa ?? ""}
					/>
				</label>
				<label>
					숙소 메모
					<input
						name="note"
						maxLength={500}
						defaultValue={trip.stay?.note ?? ""}
					/>
				</label>
				<button type="submit" className="button secondary">
					숙소 저장
				</button>
			</form>
			<p role="status">{notice}</p>
		</details>
	);
}
export function ReturnTime({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const officialMinutes = Math.max(
		0,
		...(
			editions[
				trip.destination as keyof typeof editions
			]?.branches[0]?.minutes.match(/\d+/g) ?? []
		).map(Number),
	);
	const [min, setMin] = useState(
			trip.returnTransport?.minutes ??
				(officialMinutes > 0 && officialMinutes < 1440 ? officialMinutes : 50),
		),
		[buffer, setBuffer] = useState(trip.returnTransport?.buffer ?? 120);
	let result = "";
	try {
		result = leaveForAirport(
			trip.travelContext?.departureTime ?? "",
			min,
			buffer,
		);
	} catch {}
	return (
		<details className="workspace-disclosure">
			<summary>마지막 날 숙소 출발 시각 계산</summary>
			{trip.travelContext?.departureTime ? (
				<>
					<p>
						귀국편 {trip.travelContext.departureTime} · {result}까지 숙소 출발
					</p>
					<label>
						숙소→공항 이동 분
						<input
							type="number"
							min={0}
							max={1440}
							value={min}
							onChange={(e) => setMin(Number(e.target.value))}
						/>
					</label>
					<label>
						공항 여유 분
						<input
							type="number"
							min={0}
							max={1440}
							value={buffer}
							onChange={(e) => setBuffer(Number(e.target.value))}
						/>
					</label>
					<button
						type="button"
						className="button secondary"
						onClick={() =>
							void persist((t) => {
								t.returnTransport = { minutes: min, buffer };
							})
						}
					>
						계산 조건 저장
					</button>
					{trip.days
						.at(-1)
						?.items.some(
							(i) => i.time && i.time > result.replace("전날 ", ""),
						) && (
						<p role="alert">
							숙소 출발 뒤에 일정이 있어요. 귀국일 일정을 확인해 주세요.
						</p>
					)}
					<p>
						가정값이에요. 이동 시간은 공식 안내로 바꿔 적고 항공사 안내를
						따르세요.
					</p>
				</>
			) : (
				<p>숙소 동네·도착·귀국 시간에서 귀국편 시각을 적으면 계산해요.</p>
			)}
		</details>
	);
}
export function PreparationTools({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const [chosen, setChosen] = useState<string[]>([]),
		[alarms, setAlarms] = useState([7, 3, 1, 0]),
		[notice, setNotice] = useState("");
	const suggestions = packingSuggestions(
			Number(trip.startDate.slice(5, 7)),
			trip.days.length,
			trip.companionType,
			trip.destination,
		),
		timeline = preparationDue(trip, new Date().toISOString().slice(0, 10));
	return (
		<div className="preparation-tools">
			<h3>출발까지 할 일</h3>
			<ol>
				{[30, 7, 3, 1, 0].map((offset) => (
					<li key={offset}>
						<strong>{offset ? `D-${offset}` : "당일"}</strong>
						{timeline
							.filter((c) => c.offset === offset)
							.map((c) => (
								<p key={c.id}>
									{c.overdue ? "마감 지남 · " : ""}
									{c.title}
								</p>
							))}
					</li>
				))}
			</ol>
			{trip.checklist.map((c) => (
				<label key={c.id}>
					{c.title} 마감
					<select
						value={
							c.dueOffsetDays ??
							timeline.find((x) => x.id === c.id)?.offset ??
							1
						}
						onChange={(e) =>
							void persist((t) => {
								const item = t.checklist.find((x) => x.id === c.id);
								if (item) item.dueOffsetDays = Number(e.target.value);
							})
						}
					>
						{[30, 7, 3, 1, 0].map((n) => (
							<option value={n} key={n}>
								{n ? `출발 ${n}일 전` : "출발 당일"}
							</option>
						))}
					</select>
				</label>
			))}
			<details className="workspace-disclosure">
				<summary>우리 여행에 맞는 준비물 더 보기</summary>
				<label>
					함께 가는 유형
					<select
						value={trip.companionType ?? "other"}
						onChange={(e) =>
							void persist((t) => {
								t.companionType = e.target.value as NonNullable<
									TripWorkspace["companionType"]
								>;
							})
						}
					>
						<option value="other">공통 준비물</option>
						<option value="baby">아기 동반</option>
						<option value="parents">부모님 동반</option>
						<option value="friends">친구와</option>
					</select>
				</label>
				{suggestions.map((s) => {
					const exists = trip.checklist.some((c) => c.id === `packing-${s.id}`);
					return (
						<label key={s.id}>
							<input
								type="checkbox"
								disabled={exists}
								checked={chosen.includes(s.id) || exists}
								onChange={(e) =>
									setChosen(
										e.target.checked
											? [...chosen, s.id]
											: chosen.filter((id) => id !== s.id),
									)
								}
							/>
							{s.title}
							{exists ? " · 이미 담음" : ""}
						</label>
					);
				})}
				<button
					type="button"
					className="button secondary"
					disabled={!chosen.length}
					onClick={async () => {
						if (
							(await persist((t) => {
								suggestions
									.filter(
										(s) =>
											chosen.includes(s.id) &&
											!t.checklist.some((c) => c.id === `packing-${s.id}`),
									)
									.forEach((s) =>
										t.checklist.push({
											id: `packing-${s.id}`,
											title: s.title,
											group: "personal",
											done: false,
											officialUrl: null,
											dueOffsetDays: 3,
										}),
									);
							})) !== false
						) {
							setChosen([]);
							setNotice("고른 준비물만 담았어요.");
						}
					}}
				>
					고른 준비물 담기
				</button>
			</details>
			<div className="print-checklist">
				<h2>{trip.destinationCity} 출발 준비</h2>
				{trip.checklist.map((c) => (
					<p key={c.id}>
						□ {c.title} {c.done ? "✓" : ""}
					</p>
				))}
			</div>
			<button
				type="button"
				className="button secondary"
				onClick={() => {
					try {
						window.print();
					} catch {
						setNotice("인쇄를 열지 못했어요. 글로 복사를 이용해 주세요.");
					}
				}}
			>
				인쇄·PDF로 저장
			</button>
			<CopyText
				text={trip.checklist
					.map((c) => `${c.done ? "☑" : "□"} ${c.title}`)
					.join("\n")}
				label="준비물 글로 복사"
			/>
			<details id="calendar" className="workspace-disclosure" ref={(element) => { if (element && window.location.hash === "#calendar") element.open = true; }} tabIndex={-1}>
				<summary>
					{isAppsInTossChannel() ? "출발 일정 글 복사" : "캘린더에 넣기"}
				</summary>
				{isAppsInTossChannel() ? (
					<CopyText
						text={trip.days
							.map(
								(d) => `${d.date} ${d.items.map((i) => i.title).join(" → ")}`,
							)
							.join("\n")}
						label="출발 일정 글 복사"
					/>
				) : /KAKAOTALK/i.test(navigator.userAgent) ? (
					<>
						<p>카톡 메뉴에서 다른 브라우저로 열어 주세요.</p>
						<CopyText text={location.href} label="현재 주소 복사" />
					</>
				) : (
					<>
						<p>
							출발 전 오전 9시·출발일 오전 7시 알림을 캘린더에 담아요. 캘린더
							앱에서 알림이 켜져 있는지 확인해 주세요. 날짜가 달라지면 다시
							넣어요.
						</p>
						{[7, 3, 1, 0].map((n) => (
							<label key={n}>
								<input
									type="checkbox"
									checked={alarms.includes(n)}
									onChange={(e) =>
										setAlarms(
											e.target.checked
												? [...alarms, n]
												: alarms.filter((x) => x !== n),
										)
									}
								/>
								{n ? `D-${n} 오전 9시` : "출발일 오전 7시"}
							</label>
						))}
						<button
							type="button"
							className="button secondary"
							onClick={async () => {
								downloadTripFile(
									"일본골목-여행.ics",
									tripCalendar(trip, alarms),
									"text/calendar;charset=utf-8",
								);
								await persist((t) => {
									t.calendarAddedAt = new Date().toISOString();
								});
								track("calendar_added");
							}}
						>
							캘린더 파일 받기
						</button>
						{trip.calendarAddedAt && (
							<p>
								마지막 캘린더 파일 생성 {trip.calendarAddedAt.slice(0, 10)} ·
								날짜를 바꾸면 다시 넣어 주세요.
							</p>
						)}
					</>
				)}
			</details>
			<ReturnTime trip={trip} persist={persist} />
			<p role="status">{notice}</p>
		</div>
	);
}
